/**
 * Banner sutil que aparece cuando el visitante está en modo "preview admin"
 * (sessionStorage.admin_preview_country setado). Solo visible para el admin
 * que está auditando cómo se ve el sitio para cada segmento.
 *
 * Activación: navegar a `/?preview=international` o `/?preview=argentina`
 * desde la tarjeta Hero CTA del admin.
 *
 * Salida: click en "Salir" → /?preview=exit limpia los storages y recarga.
 */

import { useEffect, useState } from "react";
import { Eye, X } from "lucide-react";

export function AdminPreviewBanner() {
  const [previewCountry, setPreviewCountry] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const sync = () => {
      try {
        const stored = sessionStorage.getItem("admin_preview_country");
        if (stored && /^[A-Z]{2}$/.test(stored)) {
          setPreviewCountry(stored);
        } else {
          setPreviewCountry(null);
        }
      } catch {
        setPreviewCountry(null);
      }
    };

    sync();
    // Sticky may be cleared by useTemplateSelection after mount (live AR lock).
    const t = window.setTimeout(sync, 50);
    const t2 = window.setTimeout(sync, 300);
    window.addEventListener("storage", sync);
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(t2);
      window.removeEventListener("storage", sync);
    };
  }, []);

  if (!previewCountry) return null;

  const segmentLabel = previewCountry === "AR" ? "Argentina" : `Internacional (${previewCountry})`;

  return (
    <div
      className="fixed top-0 inset-x-0 z-[10000] pointer-events-none flex justify-center px-3 pt-[max(env(safe-area-inset-top,0px),0.5rem)]"
      aria-live="polite"
    >
      <div className="pointer-events-auto inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-500/15 px-3 py-1.5 text-[11px] font-medium text-amber-100 backdrop-blur-md shadow-lg">
        <Eye className="h-3 w-3" strokeWidth={2} />
        <span className="leading-none">
          Vista previa admin · <span className="font-semibold">{segmentLabel}</span>
        </span>
        <a
          href="/?preview=exit"
          className="ml-1 inline-flex items-center gap-0.5 rounded-full border border-amber-400/40 bg-amber-500/20 px-1.5 py-0.5 text-[10px] text-amber-50 transition hover:bg-amber-500/30"
        >
          <X className="h-2.5 w-2.5" strokeWidth={2.5} />
          Salir
        </a>
      </div>
    </div>
  );
}
